import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ContactFormData } from '../types';
import { track } from '@vercel/analytics/react';
import { trackMetaEvent } from '../lib/metaPixel';
import { EmailService } from '../services/email.service';

/**
 * Hook personalizado para el formulario de contacto
 * Envía el mensaje vía EmailService (Edge Function send-email → Resend)
 */
export const useContactForm = (origin = 'Formulario de contacto principal') => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactFormData>();

  // Validación de email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Mostrar mensaje de estado
  const showMessage = useCallback((type: 'success' | 'error', message: string) => {
    setSubmitStatus({ type, message });
    
    // Auto-ocultar mensaje después de 5 segundos
    setTimeout(() => {
      setSubmitStatus({ type: null, message: '' });
    }, 5000);
  }, []);

  // Función para enviar el formulario
  const onSubmit = useCallback(async (data: ContactFormData) => {
    if (isSubmitting) return;

    // Validaciones adicionales
    if (!data.name.trim()) {
      showMessage('error', 'Por favor, ingresa tu nombre');
      return;
    }

    // Email obligatorio (campaña Meta de formulario, 2026-10-29).
    if (!data.email.trim() || !validateEmail(data.email.trim())) {
      showMessage('error', 'Por favor, ingresa un correo electrónico válido');
      return;
    }

    if (!data.phone.trim()) {
      showMessage('error', 'Por favor, ingresa tu número de teléfono');
      return;
    }

    if (!data.reason.trim()) {
      showMessage('error', 'Por favor, elige el servicio que te interesa');
      return;
    }

    if (!data.consent) {
      showMessage('error', 'Debes aceptar la política de privacidad para enviar el formulario');
      return;
    }

    setIsSubmitting(true);

    try {
      await EmailService.sendContactForm({
        name:   data.name,
        // Payload sin cambios de forma: la Edge Function send-email
        // (contact-form-admin) espera los 5 campos como string.
        email:  data.email.trim(),
        phone:  data.phone,
        reason: `Le interesa: ${data.reason}\nAceptó la política de privacidad (/privacidad, v2026-10-21).`,
        origin,
      });

      // Sin datos del usuario en el evento.
      track('cta_click', { canal: 'formulario', ubicacion: origin });
      trackMetaEvent('Lead');

      showMessage('success', '¡Mensaje enviado con éxito! Nos pondremos en contacto contigo pronto.');
      
      // Limpiar formulario
      reset();
      
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      showMessage('error', 'Hubo un problema al enviar el mensaje. Intenta nuevamente o contáctanos por WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, showMessage, reset, origin]);

  // Limpiar mensaje de estado
  const clearMessage = useCallback(() => {
    setSubmitStatus({ type: null, message: '' });
  }, []);

  return {
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,
    submitStatus,
    clearMessage,
    validateEmail
  };
};